/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Inject,
  Input,
  Optional,
  Output,
  ViewEncapsulation
} from '@angular/core';
import {MdCalendarCell} from './calendar-body';
import {DateAdapter} from '../core/datetime/index';
import {createMissingDateImplError} from './datepicker-errors';
import {MD_DATE_FORMATS, MdDateFormats} from '../core/datetime/date-formats';


const /** @type {?} */ DAYS_PER_WEEK = 7;
/**
 * An internal component used to display a single month in the datepicker.
 * \@docs-private
 */
export class MdMonthView<D> implements AfterContentInit {
/**
 * The date to display in this month view (everything other than the month and year is ignored).
 * @return {?}
 */
get activeDate(): D { return this._activeDate; }
/**
 * @param {?} value
 * @return {?}
 */
set activeDate(value: D) {
    let /** @type {?} */ oldActiveDate = this._activeDate;
    this._activeDate = value || this._dateAdapter.today();
    if (!this._hasSameMonthAndYear(oldActiveDate, this._activeDate)) {
      this._init();
    }
  }
private _activeDate: D;
/**
 * The currently selected date.
 * @return {?}
 */
get selected(): D { return this._selected; }
/**
 * @param {?} value
 * @return {?}
 */
set selected(value: D) {
    this._selected = value;
    this._selectedDate = this._getDateInCurrentMonth(this.selected);
  }
private _selected: D;
/**
 * A function used to filter which dates are selectable.
 */
dateFilter: (date: D) => boolean;
/**
 * Emits when a new date is selected.
 */
selectedChange = new EventEmitter<D>();
/**
 * The label for this month (e.g. "January 2017").
 */
_monthLabel: string;
/**
 * Grid of calendar cells representing the dates of the month.
 */
_weeks: MdCalendarCell[][];
/**
 * The number of blank cells in the first row before the 1st of the month.
 */
_firstWeekOffset: number;
/**
 * The date of the month that the currently selected Date falls on.
 * Null if the currently selected Date is in another month.
 */
_selectedDate: number;
/**
 * The date of the month that today falls on. Null if today is in another month.
 */
_todayDate: number;
/**
 * The names of the weekdays.
 */
_weekdays: {long: string, narrow: string}[];
/**
 * @param {?} _dateAdapter
 * @param {?} _dateFormats
 */
constructor(
public _dateAdapter: DateAdapter<D>,
private _dateFormats: MdDateFormats) {
    if (!this._dateAdapter) {
      throw createMissingDateImplError('DateAdapter');
    }
    if (!this._dateFormats) {
      throw createMissingDateImplError('MD_DATE_FORMATS');
    }

    const firstDayOfWeek = this._dateAdapter.getFirstDayOfWeek();
    const narrowWeekdays = this._dateAdapter.getDayOfWeekNames('narrow');
    const longWeekdays = this._dateAdapter.getDayOfWeekNames('long');

    // Rotate the labels for days of the week based on the configured first day of the week.
    let weekdays = longWeekdays.map((long, i) => {
      return {long, narrow: narrowWeekdays[i]};
    });
    this._weekdays = weekdays.slice(firstDayOfWeek).concat(weekdays.slice(0, firstDayOfWeek));

    this._activeDate = this._dateAdapter.today();
  }
/**
 * @return {?}
 */
ngAfterContentInit(): void {
    this._init();
  }
/**
 * Handles when a new date is selected.
 * @param {?} date
 * @return {?}
 */
_dateSelected(date: number) {
    if (this._selectedDate == date) {
      return;
    }
    this.selectedChange.emit(this._dateAdapter.createDate(
        this._dateAdapter.getYear(this.activeDate), this._dateAdapter.getMonth(this.activeDate),
        date));
  }
/**
 * Initializes this month view.
 * @return {?}
 */
private _init() {
    this._selectedDate = this._getDateInCurrentMonth(this.selected);
    this._todayDate = this._getDateInCurrentMonth(this._dateAdapter.today());
    this._monthLabel =
        this._dateAdapter.getMonthNames('short')[this._dateAdapter.getMonth(this.activeDate)]
            .toLocaleUpperCase();

    let /** @type {?} */ firstOfMonth = this._dateAdapter.createDate(this._dateAdapter.getYear(this.activeDate),
        this._dateAdapter.getMonth(this.activeDate), 1);
    this._firstWeekOffset =
        (DAYS_PER_WEEK + this._dateAdapter.getDayOfWeek(firstOfMonth) -
         this._dateAdapter.getFirstDayOfWeek()) % DAYS_PER_WEEK;

    this._createWeekCells();
  }
/**
 * Creates MdCalendarCells for the dates in this month.
 * @return {?}
 */
private _createWeekCells() {
    let /** @type {?} */ daysInMonth = this._dateAdapter.getNumDaysInMonth(this.activeDate);
    let /** @type {?} */ dateNames = this._dateAdapter.getDateNames();
    this._weeks = [[]];
    for (let /** @type {?} */ i = 0, /** @type {?} */ cell = this._firstWeekOffset; i < daysInMonth; i++, cell++) {
      if (cell == DAYS_PER_WEEK) {
        this._weeks.push([]);
        cell = 0;
      }
      let /** @type {?} */ date = this._dateAdapter.createDate(
          this._dateAdapter.getYear(this.activeDate),
          this._dateAdapter.getMonth(this.activeDate), i + 1);
      let /** @type {?} */ enabled = !this.dateFilter ||
          this.dateFilter(date);
      let /** @type {?} */ ariaLabel = this._dateAdapter.format(date, this._dateFormats.display.dateA11yLabel);
      this._weeks[this._weeks.length - 1]
          .push(new MdCalendarCell(i + 1, dateNames[i], ariaLabel, enabled));
    }
  }
/**
 * Gets the date in this month that the given Date falls on.
 * Returns null if the given Date is in another month.
 * @param {?} date
 * @return {?}
 */
private _getDateInCurrentMonth(date: D): number {
    return this._hasSameMonthAndYear(date, this.activeDate) ?
        this._dateAdapter.getDate(date) : null;
  }
/**
 * Checks whether the 2 dates are non-null and fall within the same month of the same year.
 * @param {?} d1
 * @param {?} d2
 * @return {?}
 */
private _hasSameMonthAndYear(d1: D, d2: D): boolean {
    return !!(d1 && d2 && this._dateAdapter.getMonth(d1) == this._dateAdapter.getMonth(d2) &&
              this._dateAdapter.getYear(d1) == this._dateAdapter.getYear(d2));
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  moduleId: module.id,
  selector: 'md-month-view',
  templateUrl: 'month-view.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: DateAdapter, decorators: [{ type: Optional }, ]},
{type: undefined, decorators: [{ type: Optional }, { type: Inject, args: [MD_DATE_FORMATS, ] }, ]},
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'activeDate': [{ type: Input },],
'selected': [{ type: Input },],
'dateFilter': [{ type: Input },],
'selectedChange': [{ type: Output },],
};
}

function MdMonthView_tsickle_Closure_declarations() {
/** @type {?} */
MdMonthView.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdMonthView.ctorParameters;
/** @type {?} */
MdMonthView.propDecorators;
/** @type {?} */
MdMonthView.prototype._activeDate;
/** @type {?} */
MdMonthView.prototype._selected;
/**
 * A function used to filter which dates are selectable.
 * @type {?}
 */
MdMonthView.prototype.dateFilter;
/**
 * Emits when a new date is selected.
 * @type {?}
 */
MdMonthView.prototype.selectedChange;
/**
 * The label for this month (e.g. "January 2017").
 * @type {?}
 */
MdMonthView.prototype._monthLabel;
/**
 * Grid of calendar cells representing the dates of the month.
 * @type {?}
 */
MdMonthView.prototype._weeks;
/**
 * The number of blank cells in the first row before the 1st of the month.
 * @type {?}
 */
MdMonthView.prototype._firstWeekOffset;
/**
 * The date of the month that the currently selected Date falls on.
 * Null if the currently selected Date is in another month.
 * @type {?}
 */
MdMonthView.prototype._selectedDate;
/**
 * The date of the month that today falls on. Null if today is in another month.
 * @type {?}
 */
MdMonthView.prototype._todayDate;
/**
 * The names of the weekdays.
 * @type {?}
 */
MdMonthView.prototype._weekdays;
/** @type {?} */
MdMonthView.prototype._dateAdapter;
/** @type {?} */
MdMonthView.prototype._dateFormats;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
